import { ConversationMessage } from "@swivl-mono/types";

/**
 * Shared message-stream core — pure functions for the delivery/seen tick state
 * and unread counting that both the user widget (`useChat`) and the agent
 * console operate on, now that both speak `ConversationMessage` (Phase 1b).
 * Logic lives here once instead of being reimplemented (and drifting) on each
 * side. Pure + side-effect-free → unit-tested directly.
 */

// User-perspective tick model (Figma 201:11782): ticks live on the
// user's own (user) messages — "did my message reach the recipient?".
// Walks "sent" → "delivered" (server acked the turn) → "seen" (the recipient
// replied). Forward-only.
const TICK_RANK: Record<string, number> = { sent: 0, delivered: 1, seen: 2 };

/** Advance every user message's tick forward to `to` (never downgrades). */
export function advanceUserTicks(messages: ConversationMessage[], to: "delivered" | "seen"): ConversationMessage[] {
  return messages.map((m) => {
    if (m.role !== "user") return m;
    const cur = m.deliveryStatus ?? "sent";
    return (TICK_RANK[to] ?? 0) > (TICK_RANK[cur] ?? 0) ? { ...m, deliveryStatus: to } : m;
  });
}

/**
 * On history load, derive each past user message's tick from the transcript:
 * a user message with any later assistant message was responded to → "seen";
 * a trailing user message with no reply yet is at least "delivered" (the server
 * holds it). `seenBy.members` (a human agent opened it) already reads as seen.
 */
export function deriveHistoryUserTicks(messages: ConversationMessage[]): ConversationMessage[] {
  const result = [...messages];
  let assistantAfter = false;
  for (let i = result.length - 1; i >= 0; i--) {
    const m = result[i];
    if (m.role === "assistant") {
      assistantAfter = true;
      continue;
    }
    if (m.role === "user" && !m.seenBy?.members && m.deliveryStatus !== "seen") {
      result[i] = { ...m, deliveryStatus: assistantAfter ? "seen" : (m.deliveryStatus ?? "delivered") };
    }
  }
  return result;
}

/**
 * Unread count = assistant (bot/agent) messages with a messageIndex newer than
 * the highest the reader has actually seen. Drives the widget's launcher badge;
 * the agent console can use the same for its own unread indicators.
 */
export function computeUnreadCount(messages: ConversationMessage[], lastReadIndex: number): number {
  return messages.filter((m) => m.role === "assistant" && m.messageIndex > lastReadIndex).length;
}

// ─── Stream ingest core (ENG-2825: R2 gap detection / R5 idempotent send / R8 status buffering) ───
//
// Thread-aware (ENG-3036): a session can span CONVERSATIONS (each takeover is
// its own conversation) and `messageIndex` restarts per conversation — so
// index-based identity, ordering, and gap detection are all scoped to the
// message's `botConversationId`. Ordering across threads uses the conversation
// id itself (monotonic — a newer conversation always has a bigger id), NEVER a
// timestamp (Chuck, 2026-08-17). A message without a botConversationId
// (optimistic sends, legacy payloads) matches any thread — exactly the old
// single-thread behavior.

/** Same-thread test with absent-id wildcard (optimistic/legacy messages). */
function sameThread(a?: number, b?: number): boolean {
  return a == null || b == null || a === b;
}

/** One delivery/seen change from the `message-status` room event. */
export interface StreamStatusUpdate {
  messageId?: string;
  messageIndex: number;
  /** Thread scope for the index match (absent → any thread, legacy). */
  botConversationId?: number;
  deliveryStatus?: string;
  seenBy?: ConversationMessage["seenBy"];
}

export interface IngestResult {
  messages: ConversationMessage[];
  /** The message was actually added (not a duplicate / echo of an optimistic copy it replaced). */
  added: boolean;
  /**
   * The incoming messageIndex skipped ahead of the contiguous known stream —
   * one or more messages were lost in transit (dropped emit, crash between
   * save and publish). Caller must trigger a catch-up delta fetch (R2).
   */
  gapDetected: boolean;
}

/**
 * Highest server-confirmed messageIndex in the list (-1 when none). Pass a
 * botConversationId to scope the answer to that thread (messages without one
 * count toward every thread — legacy wildcard).
 */
export function highestKnownIndex(messages: ConversationMessage[], botConversationId?: number): number {
  let max = -1;
  for (const m of messages) {
    if (!sameThread(m.botConversationId, botConversationId)) continue;
    if (m.messageIndex > max) max = m.messageIndex;
  }
  return max;
}

/**
 * Sort confirmed messages by (botConversationId, messageIndex) — thread ids
 * are monotonic, so cross-thread order is creation order; within a thread this
 * is exactly the old index sort. Messages without a thread id sort first
 * (they're always the session's oldest thread). Optimistic (-1) copies stay at
 * the end in arrival order.
 */
function sortStream(messages: ConversationMessage[]): ConversationMessage[] {
  const confirmed = messages
    .filter((m) => m.messageIndex >= 0)
    .sort((a, b) => {
      const threadA = a.botConversationId ?? -1;
      const threadB = b.botConversationId ?? -1;
      return threadA !== threadB ? threadA - threadB : a.messageIndex - b.messageIndex;
    });
  const optimistic = messages.filter((m) => m.messageIndex < 0);
  return [...confirmed, ...optimistic];
}

/**
 * Ingest one live message pushed over the socket. Dedup rules, in order:
 *  1. `messageId` matches an optimistic copy (messageIndex -1) → the echo of
 *     our own send: replace the optimistic copy with the server truth (R5).
 *  2. `messageId` or a confirmed `messageIndex` already present → duplicate
 *     (live push + catch-up fetch overlap): drop.
 * Reports `gapDetected` when the message lands more than one index past the
 * highest known — the caller runs the delta fetch; the message itself is
 * still ingested (no waiting on the fetch).
 */
export function ingestMessage(messages: ConversationMessage[], incoming: ConversationMessage): IngestResult {
  // Index identity and gap detection are THREAD-scoped (ENG-3036): a new
  // conversation's indexes restart at 0 and must never collide with (or look
  // like a replay of) an earlier thread's messages in the same session.
  const prevMax = highestKnownIndex(messages, incoming.botConversationId);

  const optimisticIdx = messages.findIndex((m) => m.messageIndex < 0 && m.messageId === incoming.messageId);
  if (optimisticIdx >= 0) {
    const next = [...messages];
    next[optimisticIdx] = incoming;
    return { messages: sortStream(next), added: false, gapDetected: false };
  }

  const duplicate = messages.some(
    (m) =>
      m.messageId === incoming.messageId ||
      (incoming.messageIndex >= 0 && m.messageIndex === incoming.messageIndex && sameThread(m.botConversationId, incoming.botConversationId))
  );
  if (duplicate) return { messages, added: false, gapDetected: false };

  const gapDetected = prevMax >= 0 && incoming.messageIndex > prevMax + 1;
  return { messages: sortStream([...messages, incoming]), added: true, gapDetected };
}

/**
 * Merge a fetched batch (history load or R2/R3 catch-up delta) into the
 * stream. Same dedup rules as {@link ingestMessage}; optimistic copies are
 * reconciled by messageId — this is what resolves a send whose HTTP response
 * was lost (R5): the retry/catch-up returns the confirmed row and the pending
 * copy stops being pending.
 */
export function mergeFetchedMessages(
  messages: ConversationMessage[],
  fetched: ConversationMessage[]
): { messages: ConversationMessage[]; added: number } {
  let current = messages;
  let added = 0;
  for (const msg of fetched) {
    const result = ingestMessage(current, msg);
    current = result.messages;
    if (result.added) added += 1;
  }
  return { messages: current, added };
}

/**
 * Apply delivery/seen updates to the stream. Updates whose target message is
 * not present yet (the status event raced ahead of the message itself) are
 * returned as `unmatched` — the caller buffers them and re-applies after the
 * message arrives (R8) instead of dropping them.
 */
export function ingestStatusUpdates(
  messages: ConversationMessage[],
  updates: StreamStatusUpdate[]
): { messages: ConversationMessage[]; unmatched: StreamStatusUpdate[] } {
  if (updates.length === 0) return { messages, unmatched: [] };
  const unmatched: StreamStatusUpdate[] = [];
  let touched = false;

  const next = messages.map((m) => m);
  for (const u of updates) {
    const idx = next.findIndex(
      (m) =>
        (u.messageId != null && m.messageId === u.messageId) ||
        (m.messageIndex >= 0 && m.messageIndex === u.messageIndex && sameThread(m.botConversationId, u.botConversationId))
    );
    if (idx === -1) {
      unmatched.push(u);
      continue;
    }
    next[idx] = {
      ...next[idx],
      ...(u.deliveryStatus ? { deliveryStatus: u.deliveryStatus } : {}),
      ...(u.seenBy ? { seenBy: u.seenBy } : {})
    };
    touched = true;
  }
  return { messages: touched ? next : messages, unmatched };
}

/** Mark an optimistic copy failed (HTTP send rejected) — kept visible for retry UI. */
export function failOptimistic(messages: ConversationMessage[], messageId: string): ConversationMessage[] {
  return messages.map((m) => (m.messageIndex < 0 && m.messageId === messageId ? { ...m, deliveryStatus: "failed" } : m));
}

/** Confirm an optimistic copy from the HTTP send response (id + allocated messageIndex). */
export function confirmOptimistic(
  messages: ConversationMessage[],
  messageId: string,
  confirmed: { id?: number; messageIndex: number; deliveryStatus?: string }
): ConversationMessage[] {
  const next = messages.map((m) =>
    m.messageIndex < 0 && m.messageId === messageId
      ? { ...m, id: confirmed.id ?? m.id, messageIndex: confirmed.messageIndex, deliveryStatus: confirmed.deliveryStatus ?? m.deliveryStatus }
      : m
  );
  return sortStream(next);
}
